import { zodResolver } from "@hookform/resolvers/zod";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  FormControlLabel,
  Grid2,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useFormStyles } from "../../hooks/useFormStyles";
import { getUpdateUserFormSchema, getUpdateUserPasswordFormSchema, UpdateUserForm, UpdateUserPasswordForm } from "../../types/forms/admin/user";
import { Role } from "../../types/interfaces/account";
import {
  UpdateUserPasswordRequest,
  UpdateUserRequest,
  User,
} from "../../types/model/user";

interface ManageUserFormProps {
  userData: User;
  onUpdate: (values: UpdateUserRequest) => Promise<boolean>;
  onUpdatePassword: (values: UpdateUserPasswordRequest) => Promise<void>;
  onDelete: () => Promise<void>;
  availableRoles: Role[];
  isActive: boolean;
}

export default function ManageUserForm({
  userData,
  onUpdate,
  onUpdatePassword,
  onDelete,
  availableRoles,
  isActive,
}: Readonly<ManageUserFormProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [updatePassword, setUpdatePassword] = useState(false);

  const formStyles = useFormStyles(isEditing || updatePassword);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const updateUserValidationSchema = getUpdateUserFormSchema(t);
  const updateUserPasswordValidationSchema = getUpdateUserPasswordFormSchema(t);

  const updateUserMethods = useForm<UpdateUserForm>({
    resolver: zodResolver(updateUserValidationSchema),
    defaultValues: {
      name: userData.name || "",
      email: userData.email || "",
      phoneNumber: userData.phoneNumber || "",
      roleId: userData.roleId ?? 0,
      enabled: userData.enabled ?? false,
    },
  });

  const updateUserPasswordMethods = useForm<UpdateUserPasswordForm>({
    resolver: zodResolver(updateUserPasswordValidationSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const handleCancelEdit = () => {
    updateUserMethods.reset();
    setIsEditing(false);
  };

  const handleDeleteClick = async () => {
    try {
      await onDelete();
      navigate(-1);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmitUser = async (values: UpdateUserForm) => {
    const success = await onUpdate(values);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleSubmitPassword = async (values: UpdateUserPasswordForm) => {
  try {
    await onUpdatePassword(values);

    updateUserPasswordMethods.reset();
    setShowPassword(false);
    setShowConfirm(false);
    setUpdatePassword(false);
  } catch (error) {
    console.error(error);
  }
};

  const handleCancelPassword = () => {
    setUpdatePassword(false);
    updateUserPasswordMethods.reset();
    setShowPassword(false);
    setShowConfirm(false);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      {/* User Details Form */}
      <FormProvider {...updateUserMethods}>
        <Box component="form" id="user-details-form" onSubmit={updateUserMethods.handleSubmit(handleSubmitUser)}>
          <Toolbar sx={{ mb: 3 }}>
            <Typography sx={{ flex: "1 1 100%" }} variant="h4">
              {t("admin.users.user-information")}
            </Typography>

            <Tooltip title={t("generic.back")}>
              <Button
                onClick={() => navigate(-1)}
                variant="contained"
                sx={{ borderRadius: 2 }}
              >
                <ArrowBackIcon />
              </Button>
            </Tooltip>
          </Toolbar>

          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <TextField
                fullWidth
                id="name"
                label={`${t("admin.users.name")} *`}
                variant={isEditing ? "outlined" : "standard"}
                disabled={!isEditing}
                sx={{ ...formStyles }}
                {...updateUserMethods.register("name")}
                error={!!updateUserMethods.formState.errors.name}
                helperText={updateUserMethods.formState.errors.name?.message as any}
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                fullWidth
                id="email"
                label={`${t("admin.users.email")} *`}
                variant={isEditing ? "outlined" : "standard"}
                disabled={!isEditing}
                sx={{ ...formStyles }}
                {...updateUserMethods.register("email")}
                error={!!updateUserMethods.formState.errors.email}
                helperText={updateUserMethods.formState.errors.email?.message as any}

              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                fullWidth
                id="phoneNumber"
                label={t("admin.users.phone-number")}
                variant={isEditing ? "outlined" : "standard"}
                disabled={!isEditing}
                sx={{ ...formStyles }}
                {...updateUserMethods.register("phoneNumber")}
                error={!!updateUserMethods.formState.errors.phoneNumber}
                helperText={updateUserMethods.formState.errors.phoneNumber?.message as any}

              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="roleId"
                control={updateUserMethods.control}
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    label={`${t("admin.users.role")} *`}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing}
                    sx={{ ...formStyles }}
                    {...field}
                    error={!!updateUserMethods.formState.errors.roleId}
                    helperText={updateUserMethods.formState.errors.roleId?.message as any}
                  >
                    {availableRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {t(role.i18nLabel)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="enabled"
                control={updateUserMethods.control}
                render={({ field }) => (
                  <FormControlLabel
                    disabled={!isEditing}
                    control={<Switch {...field} checked={field.value} />}
                    label={field.value ? t("generic.enabled") : t("generic.disabled")}
                    labelPlacement="end"
                  />
                )}
              />
            </Grid2>
          </Grid2>
        </Box>
      </FormProvider>

      {/* Action Buttons */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          p: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          borderTop: updatePassword ? 1 : 0,
          borderColor: "divider",
          mt: 3,
        }}
      >
        {isActive ? (
          <>
            {!isEditing && !updatePassword && (
              <>
                <Button variant="outlined" onClick={() => setIsEditing(true)}>
                  {t("admin.users.user-edit")}
                </Button>
                <Button variant="outlined" onClick={() => setUpdatePassword(true)}>
                  {t("admin.users.user-update-password")}
                </Button>
                <Button variant="outlined" color="error" onClick={handleDeleteClick} disabled={!isActive}>
                  {t("admin.users.user-delete")}
                </Button>
              </>
            )}

            {isEditing && (
              <>
                <Button variant="outlined" color="secondary" onClick={handleCancelEdit}>
                  {t("generic.cancel")}
                </Button>
                <Button type="submit" form="user-details-form" variant="outlined">
                  {t("generic.save")}
                </Button>
              </>
            )}
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            {t("admin.users.user-deactivated-message")}
          </Typography>
        )}
      </Box>

      {/* Update Password Form */}
      {updatePassword && (
        <FormProvider {...updateUserPasswordMethods}>
          <form id="update-password-form" onSubmit={updateUserPasswordMethods.handleSubmit(handleSubmitPassword)}>
            <Grid2 container spacing={3}>
              <Grid2 size={6}>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  label={`${t("admin.users.password")} *`}
                  variant="outlined"
                  sx={{ ...formStyles }}
                  {...updateUserPasswordMethods.register("password")}
                  error={!!updateUserPasswordMethods.formState.errors.password}
                  helperText={updateUserPasswordMethods.formState.errors.password?.message}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            aria-label={
                              showPassword
                                ? t("admin.users.hide-password")
                                : t("admin.users.show-password")
                            }
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid2>

              <Grid2 size={6}>
                <TextField
                  fullWidth
                  type={showConfirm ? "text" : "password"}
                  label={`${t("admin.users.password-confirm")} *`}
                  variant="outlined"
                  sx={{ ...formStyles }}
                  {...updateUserPasswordMethods.register("confirmPassword")}
                  error={!!updateUserPasswordMethods.formState.errors.confirmPassword}
                  helperText={updateUserPasswordMethods.formState.errors.confirmPassword?.message}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirm((prev) => !prev)}
                            edge="end"
                            aria-label={
                              showConfirm
                                ? t("admin.users.hide-password")
                                : t("admin.users.show-password")
                            }
                          >
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid2>
            </Grid2>

            <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 2 }}>
              <Button 
                variant="outlined" 
                color="secondary" 
                onClick={handleCancelPassword}
              >
                {t("generic.cancel")}
              </Button>
              <Button 
                type="submit" 
                form="update-password-form" 
                variant="contained"
              >
                {t("admin.users.user-update-password")}
              </Button>
            </Box>
          </form>
        </FormProvider>
      )}
    </Paper>
  );
};